import React, { useState } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const ReservationDetailModal = ({
  isOpen,
  onClose,
  slot,
  board,
  onCancelReservation,
  onSendMessage
}) => {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelNote, setCancelNote] = useState('');
  const [sendCancelEmail, setSendCancelEmail] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [message, setMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  if (!slot || !board) return null;

  const handleClose = () => {
    setShowCancelConfirm(false);
    setCancelNote('');
    setSendCancelEmail(true);
    setShowMessageForm(false);
    setMessage('');
    onClose();
  };

  const handleCancelReservation = async () => {
    setIsCancelling(true);
    try {
      await onCancelReservation(slot.reservationId, cancelNote, sendCancelEmail);
      handleClose();
    } catch (error) {
      console.error('Error cancelling reservation:', error);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    setIsSendingMessage(true);
    try {
      await onSendMessage(slot.reservedByUserId, message);
      setMessage('');
      setShowMessageForm(false);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const formatTime = (isoString) => {
    const date = parseISO(isoString);
    return formatInTimeZone(date, board.gymTimezone || 'America/Los_Angeles', 'h:mm a');
  };

  const formatDate = (isoString) => {
    const date = parseISO(isoString);
    return formatInTimeZone(date, board.gymTimezone || 'America/Los_Angeles', 'EEEE, MMMM d, yyyy');
  };

  const getStatusBadge = () => {
    if (slot.reservationStatus === 'checked_in') {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
          Checked In
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
        Reserved
      </span>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Reservation Details"
      size="medium"
    >
      <div className="space-y-6">
        {/* User Info Section */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Customer
            </h4>
            {getStatusBadge()}
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                {slot.reservedByFirstName?.charAt(0)}{slot.reservedByLastName?.charAt(0)}
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  {slot.reservedByFirstName} {slot.reservedByLastName}
                </p>
                {slot.reservedByEmail && (
                  <p className="text-sm text-gray-500">{slot.reservedByEmail}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Reservation Details Section */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
            Reservation Info
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Board:</span>
              <span className="font-medium text-gray-900">{board.boardName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Type:</span>
              <span className="font-medium text-gray-900 capitalize">{board.boardType}</span>
            </div>
            {board.angle && (
              <div className="flex justify-between">
                <span className="text-gray-600">Angle:</span>
                <span className="font-medium text-gray-900">{board.angle}°</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Date:</span>
              <span className="font-medium text-gray-900">{formatDate(slot.startTime)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Time:</span>
              <span className="font-medium text-gray-900">
                {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
              </span>
            </div>
            {slot.checkedInAt && (
              <div className="flex justify-between">
                <span className="text-gray-600">Checked in at:</span>
                <span className="font-medium text-green-700">
                  {formatTime(slot.checkedInAt)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Message Form */}
        {showMessageForm && (
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              Send Message to {slot.reservedByFirstName}
            </h4>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={3}
            />
            <div className="flex justify-end gap-2 mt-3">
              <Button
                variant="outline"
                size="small"
                onClick={() => {
                  setShowMessageForm(false);
                  setMessage('');
                }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                onClick={handleSendMessage}
                loading={isSendingMessage}
                disabled={!message.trim() || isSendingMessage}
              >
                Send
              </Button>
            </div>
          </div>
        )}

        {/* Cancel Confirmation */}
        {showCancelConfirm && (
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <h4 className="text-sm font-semibold text-red-700 mb-3">
              Cancel Reservation
            </h4>
            <p className="text-sm text-gray-600 mb-3">
              Are you sure you want to cancel this reservation? This action cannot be undone.
            </p>
            <textarea
              value={cancelNote}
              onChange={(e) => setCancelNote(e.target.value)}
              placeholder="Reason for cancellation (optional)..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 resize-none mb-3"
              rows={2}
            />
            <label className="flex items-center gap-2 text-sm text-gray-600 mb-3">
              <input
                type="checkbox"
                checked={sendCancelEmail}
                onChange={(e) => setSendCancelEmail(e.target.checked)}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              Send cancellation email to customer
            </label>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="small"
                onClick={() => {
                  setShowCancelConfirm(false);
                  setCancelNote('');
                }}
              >
                Keep Reservation
              </Button>
              <Button
                variant="danger"
                size="small"
                onClick={handleCancelReservation}
                loading={isCancelling}
                disabled={isCancelling}
              >
                Confirm Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!showCancelConfirm && !showMessageForm && (
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              onClick={() => setShowMessageForm(true)}
              className="w-full justify-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Send Message
            </Button>
            <Button
              variant="danger"
              onClick={() => setShowCancelConfirm(true)}
              className="w-full justify-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel Reservation
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ReservationDetailModal;
